// src/admin/pages/Restaurants/EditMenuItemModal.js
import React, { useState, useEffect } from "react";
import "./EditMenuItemModal.css";

export default function EditMenuItemModal({
  item,
  onClose,
  onMenuUpdated,
  menu,
  categories,
}) {
  const [name, setName] = useState(item.name);
  const [price, setPrice] = useState(item.price);
  const [active, setActive] = useState(item.active !== false);

  const [selectedCategory, setSelectedCategory] = useState(item.category || "");
  const [newCategory, setNewCategory] = useState("");
  const [useNewCategory, setUseNewCategory] = useState(false);

  const [error, setError] = useState("");
  const [recipeItems, setRecipeItems] = useState(item.recipe || []);

  // Ingredienti caricati da /restaurant_ingredients
  const [availableIngredients, setAvailableIngredients] = useState([]);
  const [newIngredientId, setNewIngredientId] = useState("");
  const [newQty, setNewQty] = useState(1);

  useEffect(() => {
    fetchAvailableIngredients();
    // eslint-disable-next-line
  }, []);

  // Carica gli ingredienti disponibili per questo ristorante
  const fetchAvailableIngredients = async () => {
    try {
      const token = localStorage.getItem("access_token");
      const url = `${process.env.REACT_APP_API_URL}/restaurant_ingredients?restaurant_id=${menu.restaurant_id}`;
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        throw new Error("Errore caricamento ingredienti disponibili");
      }
      const data = await res.json();
      // Mappiamo i campi
      const mapped = data.map((ri) => ({
        id: ri.ingredient_id,
        name: ri.ingredient_name,
        unit: ri.unit,
      }));
      setAvailableIngredients(mapped);
    } catch (err) {
      console.error(err);
    }
  };

  // Rifetch del menu completo dopo un'operazione
  const refetchMenuAndUpdate = async () => {
    try {
      const token = localStorage.getItem("access_token");
      const fresh = await fetch(
        `${process.env.REACT_APP_API_URL}/menus/${menu.id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (!fresh.ok) {
        throw new Error("Errore caricamento menu aggiornato");
      }
      const freshMenu = await fresh.json();
      onMenuUpdated(freshMenu);
    } catch (err) {
      console.error("refetchMenuAndUpdate:", err);
    }
  };

  // Salva info base (nome, prezzo, categoria)
  const handleSaveBaseInfo = async () => {
    try {
      setError("");
      const token = localStorage.getItem("access_token");
      const finalCategory = useNewCategory ? newCategory : selectedCategory;
      const categoryToSend = finalCategory ? finalCategory.trim() : null;

      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/menu/${item.id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            name,
            price: parseFloat(price),
            active,
            category: categoryToSend,
          }),
        }
      );
      if (!res.ok) {
        const errData = await res.json();
        throw new Error(errData.detail || "Errore aggiornamento piatto");
      }

      // Aggiorna localmente la scheda (opzionale)
      await refetchMenuAndUpdate();
      onClose();
    } catch (err) {
      setError(err.message);
    }
  };

  // Rimuovi ingrediente da ricetta
  const handleRemoveIngredient = async (recipeItem) => {
    if (!window.confirm(`Rimuovere ${recipeItem.ingredient_name}?`)) return;
    try {
      setError("");
      const token = localStorage.getItem("access_token");
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/recipe/${recipeItem.id}`,
        {
          method: "DELETE",
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (!res.ok) {
        const errData = await res.json();
        throw new Error(errData.detail || "Errore rimozione ingrediente");
      }

      // Aggiorniamo subito lo stato locale
      setRecipeItems((prev) => prev.filter((ri) => ri.id !== recipeItem.id));
      await refetchMenuAndUpdate();
    } catch (err) {
      alert(err.message);
    }
  };

  // Aggiorna qty di un ingrediente
  const handleUpdateQty = async (recipeItem, newValue) => {
    try {
      setError("");
      const token = localStorage.getItem("access_token");
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/recipe/${recipeItem.id}`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ qty_required: parseFloat(newValue) }),
        }
      );
      if (!res.ok) {
        const errData = await res.json();
        throw new Error(errData.detail || "Errore update qty");
      }

      // Aggiorna lo stato locale immediatamente
      const updated = await res.json();
      setRecipeItems((prev) =>
        prev.map((ri) =>
          ri.id === recipeItem.id
            ? { ...ri, qty_required: updated.qty_required }
            : ri
        )
      );
      await refetchMenuAndUpdate();
    } catch (err) {
      alert(err.message);
    }
  };

  // Aggiunge nuovo ingrediente
  const handleAddNewIngredient = async () => {
    if (!newIngredientId) return;
    try {
      setError("");
      const token = localStorage.getItem("access_token");
      const res = await fetch(`${process.env.REACT_APP_API_URL}/recipe`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          menu_item_id: item.id,
          ingredient_id: parseInt(newIngredientId, 10),
          qty_required: parseFloat(newQty),
        }),
      });
      if (!res.ok) {
        const errData = await res.json();
        throw new Error(errData.detail || "Errore aggiunta ingrediente");
      }
      const createdRecipe = await res.json();

      // Troviamo i dati dell'ingrediente corrispondente
      const ingObj = availableIngredients.find(
        (ing) => ing.id === createdRecipe.ingredient_id
      );
      const newObj = {
        id: createdRecipe.id,
        ingredient_id: createdRecipe.ingredient_id,
        qty_required: createdRecipe.qty_required,
        ingredient_name: ingObj ? ingObj.name : `Ing #${createdRecipe.ingredient_id}`,
        unit: ingObj ? ingObj.unit : "",
      };

      // Aggiorna localmente la lista ricetta
      setRecipeItems((prev) => [...prev, newObj]);
      setNewIngredientId("");
      setNewQty(1);

      await refetchMenuAndUpdate();
    } catch (err) {
      alert(err.message);
    }
  };

  // Impedisce la chiusura cliccando all'interno
  const handleInnerClick = (e) => {
    e.stopPropagation();
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-container" onClick={handleInnerClick}>
        <div className="modal-header">
          <h3 className="modal-title">Modifica Piatto: {item.name}</h3>
          <button className="close-btn" onClick={onClose}>
            ✕
          </button>
        </div>

        <div className="modal-body">
          {error && <p className="error-text">{error}</p>}

          <div className="section-block">
            <h4>Dati Base</h4>
            <div className="form-row">
              <label>Nome</label>
              <input
                className="modal-input"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>

            <div className="form-row">
              <label>Prezzo</label>
              <input
                className="modal-input"
                type="number"
                step="0.01"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
              />
            </div>

            <div className="form-row">
              <label>Categoria</label>
              {!useNewCategory && (
                <select
                  className="modal-select"
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                >
                  <option value="">(Nessuna)</option>
                  {categories.map((cat) => (
                    <option key={cat} value={cat}>
                      {cat}
                    </option>
                  ))}
                </select>
              )}
              {useNewCategory && (
                <input
                  type="text"
                  placeholder="Nuova Categoria"
                  value={newCategory}
                  onChange={(e) => setNewCategory(e.target.value)}
                  className="modal-input"
                />
              )}
              <label className="checkbox-row">
                <input
                  type="checkbox"
                  checked={useNewCategory}
                  onChange={(e) => setUseNewCategory(e.target.checked)}
                />
                <span>Nuova?</span>
              </label>
            </div>

            <div className="form-row checkbox-row">
              <label>
                <input
                  type="checkbox"
                  checked={active}
                  onChange={(e) => setActive(e.target.checked)}
                />
                <span>Attivo</span>
              </label>
            </div>
          </div>

          {/* Sezione Ingredienti */}
          <div className="section-block">
            <h4>Ingredienti</h4>
            <div className="ingredient-row">
              <select
                className="modal-select"
                value={newIngredientId}
                onChange={(e) => setNewIngredientId(e.target.value)}
              >
                <option value="">-- Seleziona ingrediente --</option>
                {availableIngredients.map((ing) => (
                  <option key={ing.id} value={ing.id}>
                    {ing.name} {ing.unit ? `(${ing.unit})` : ""}
                  </option>
                ))}
              </select>
              <input
                className="modal-input-qty"
                type="number"
                step="0.01"
                value={newQty}
                onChange={(e) => setNewQty(e.target.value)}
              />
              <button
                className="add-ingredient-btn"
                onClick={handleAddNewIngredient}
              >
                + Aggiungi
              </button>
            </div>

            {recipeItems.length > 0 && (
              <ul className="recipe-modal-list">
                {recipeItems.map((ri) => (
                  <li key={ri.id} className="recipe-modal-item">
                    <div>
                      <strong>{ri.ingredient_name}</strong>
                      {ri.unit && ` (${ri.unit})`}
                    </div>
                    <div className="ingredient-actions">
                      <input
                        className="modal-input-qty"
                        type="number"
                        step="0.01"
                        value={ri.qty_required}
                        onChange={(e) => handleUpdateQty(ri, e.target.value)}
                      />
                      <button
                        className="remove-ingredient-btn"
                        onClick={() => handleRemoveIngredient(ri)}
                      >
                        Rimuovi
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="modal-actions">
            <button className="save-btn" onClick={handleSaveBaseInfo}>
              Salva Piatto
            </button>
            <button className="cancel-btn" onClick={onClose}>
              Annulla
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
